import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface SpeakerRenameEditorProps {
  resultJson: {
    speakers: Array<{
      index: number;
      speakerId: string;
      speaker: string;
      text: string;
    }>;
  };
  onSave: (mappings: Record<string, string>) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const SpeakerRenameEditor = ({
  resultJson,
  onSave,
  onCancel,
  isLoading = false
}: SpeakerRenameEditorProps) => {
  const [speakerNameMappings, setSpeakerNameMappings] = useState<Record<string, string>>({});

  // Extract unique speaker IDs in order
  const uniqueSpeakerIds = Array.from(
    new Set(resultJson.speakers.map(s => s.speakerId))
  ).sort((a, b) => {
    const aIndex = resultJson.speakers.find(s => s.speakerId === a)?.index ?? 0;
    const bIndex = resultJson.speakers.find(s => s.speakerId === b)?.index ?? 0;
    return aIndex - bIndex;
  });

  // Get current display name for speaker ID
  const getCurrentName = (speakerId: string): string => {
    const speaker = resultJson.speakers.find(s => s.speakerId === speakerId);
    return speaker?.speaker || speakerId;
  };

  // Update mapping
  const updateMapping = (speakerId: string, newName: string) => {
    setSpeakerNameMappings(prev => ({
      ...prev,
      [speakerId]: newName
    }));
  };

  // Handle save
  const handleSave = async () => {
    // Filter out unchanged names
    const changedMappings = Object.entries(speakerNameMappings).reduce((acc, [id, name]) => {
      if (name && name !== getCurrentName(id)) {
        acc[id] = name;
      }
      return acc;
    }, {} as Record<string, string>);

    if (Object.keys(changedMappings).length === 0) {
      toast.info("לא בוצעו שינויים");
      return;
    }

    await onSave(changedMappings);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          📝 עריכת שמות דוברים
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {uniqueSpeakerIds.map(speakerId => (
            <div key={speakerId} className="flex items-center gap-3">
              <div className="flex-1 space-y-1">
                <label className="text-xs text-muted-foreground font-medium">
                  {speakerId}
                </label>
                <Input
                  value={speakerNameMappings[speakerId] || ""}
                  onChange={(e) => updateMapping(speakerId, e.target.value)}
                  placeholder={getCurrentName(speakerId)}
                  disabled={isLoading}
                  className="text-right"
                />
                {speakerNameMappings[speakerId] && speakerNameMappings[speakerId] !== getCurrentName(speakerId) && (
                  <Badge variant="secondary" className="text-xs">
                    היה: {getCurrentName(speakerId)}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <Button
            onClick={onCancel}
            variant="outline"
            disabled={isLoading}
          >
            ביטול
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || Object.keys(speakerNameMappings).length === 0}
          >
            שמור שינויים
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpeakerRenameEditor;
